import React from 'react';
import withStyles from 'utils/withStyles';
import { withApollo, WithApolloClient } from '@apollo/react-hoc';
import { RouteComponentProps, withRouter, Link } from 'react-router-dom';
import { ReCaptcha } from 'recaptcha-v3-react';
import { WithTranslation, withTranslation } from 'react-i18next';

import Input from 'components/form/Input/Input';
import { isEmail } from 'utils/text';
import Label from 'components/form/Label/Label';
import NextButton from 'components/form/NextButton/NextButton';
import mutations from 'schemas/mutations.graphql';
import { ILocalizedString } from 'models/common';
import s from './ResetPassword.css';
import Form from '../Form/Form';
import PageTitle from '../../common/PageTitle/PageTitle';

interface State {
  email: string;
  emailError?: string;
  isSending: boolean;
  recaptchaToken: string;
  responseError: string;
  successMessage: string;
}

interface Props extends RouteComponentProps, WithTranslation {}

const serverUnavailableMessage = 'Сервер временно недоступен, попробуйте позже';

interface Result {
  data: {
    restorePassword: {
      isSuccess: boolean;
      errorCode?: string;
      errorMessage?: ILocalizedString;
    };
  };
}

class ResetPassword extends React.Component<WithApolloClient<Props>, State> {
  state = {
    email: '',
    emailError: null,
    isSending: false,
    recaptchaToken: '',
    responseError: '',
    successMessage: '',
  };

  onEmailChange = (e) => {
    this.setState({ email: e.target.value, emailError: null });
  };

  onEmailBlur = () => {
    const email = this.state.email.trim();
    if (email.length === 0) {
      this.setState({ emailError: 'не может быть пустым' });
    } else if (!isEmail(email)) {
      this.setState({ emailError: 'некорректное значение' });
    }
  };

  onButtonClick = async (e) => {
    const { email, recaptchaToken } = this.state;
    const { client } = this.props;
    e.preventDefault();
    const variables = {
      email,
      recaptchaResponse: recaptchaToken,
    };
    this.setState({ isSending: true, responseError: '' });

    try {
      const res = await client.mutate({
        mutation: mutations.RestorePasswordMutation,
        variables,
      });
      const { errorCode, isSuccess } = (res as Result).data.restorePassword;
      if (isSuccess) {
        this.setState({
          successMessage:
            'Мы отправили ссылку для восстановления пароля вам на почту',
          email: '',
        });
      } else {
        this.setState({ responseError: errorCode });
      }
      this.setState({ isSending: false });
    } catch (err) {
      this.setState({
        responseError: __DEV__ ? e.message : serverUnavailableMessage,
      });
    }
  };

  get isButtonDisabled(): boolean {
    const { email, emailError, isSending } = this.state;
    return email.trim().length === 0 || emailError || isSending;
  }

  verifyCallback = (recaptchaToken) => {
    this.setState({ recaptchaToken });
  };

  render() {
    const {
      email, emailError, responseError, successMessage,
    } = this.state;
    const { t } = this.props;
    return (
      <div className={s.reset}>
        <PageTitle>{t('Восстановить пароль')}</PageTitle>
        <Form>
          <div className={s.title}>
            {t('Ссылку с восстановлением пароля пришлем на почту')}
          </div>
          <form className={s.form}>
            <div className={s.row}>
              <Label htmlFor="email" error={emailError && t(emailError)}>
                {t('Email')}
              </Label>
              <Input
                value={email}
                onChange={this.onEmailChange}
                onBlur={this.onEmailBlur}
                fullWidth
                withBorder
                placeholder={t('me@example.com')}
                type="email"
                id="email"
                className={s.input}
              />
            </div>
            {responseError && (
              <div className={s.responseError}>{t(responseError)}</div>
            )}
            {successMessage && (
              <div className={s.successMessage}>{t(successMessage)}</div>
            )}
            <div className={s.buttons}>
              <ReCaptcha
                sitekey={process.env.RECAPTCHA_SITE_KEY}
                action="signin"
                verifyCallback={this.verifyCallback}
              />
              <NextButton
                fullWidth
                disabled={this.isButtonDisabled}
                onClick={this.onButtonClick}
              >
                {t('Восстановить')}
              </NextButton>
            </div>
            <div className={s.back}>
              <Link to="/auth/signin">{t('Вспомнили пароль?')}</Link>
            </div>
          </form>
        </Form>
      </div>
    );
  }
}

export default withStyles(s)(withApollo((withRouter as any)(withTranslation()(ResetPassword))));
